import { X } from 'lucide-react';
import { focusRing } from './shared';

export const EMPTY_PRODUCT_FILTERS = { brand: '', category: '', subcategory: '', occasion: '', attributes: {} };

export function productFilterParams(filters) {
  return {
    ...(filters.brand ? { brand: filters.brand } : {}),
    ...(filters.category ? { category: filters.category } : {}),
    ...(filters.subcategory ? { subcategory: filters.subcategory } : {}),
    ...(filters.occasion ? { occasion: filters.occasion } : {}),
    ...(Object.keys(filters.attributes).length ? {
      attributes: JSON.stringify(Object.fromEntries(Object.entries(filters.attributes).map(([key, value]) => [key, [value]]))),
    } : {}),
  };
}

export default function ProductFilters({ tax, filters, onChange, attribute, onAttributeChange, brands = null }) {
  const patch = (values) => onChange({ ...filters, ...values });
  const setValue = (value) => {
    const attributes = { ...filters.attributes };
    if (value) attributes[attribute] = value;
    else delete attributes[attribute];
    patch({ attributes });
  };
  const active = filters.brand || filters.category || filters.occasion || Object.keys(filters.attributes).length > 0;
  const selectClass = `h-9 min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 text-[13px] text-gray-700 disabled:bg-gray-50 disabled:text-gray-400 ${focusRing}`;
  return (
    <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3 space-y-3">
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${brands ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-2`}>
        {brands && (
          <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">Brand
            <select aria-label="Catalogue brand" className={selectClass} value={filters.brand || ''}
              onChange={(e) => patch({ brand: e.target.value })}>
              <option value="">All brands</option>
              {brands.map((b) => <option key={b}>{b}</option>)}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">Category
          <select aria-label="Catalogue category" className={selectClass} value={filters.category}
            onChange={(e) => patch({ category: e.target.value, subcategory: '' })}>
            <option value="">All categories</option>
            {Object.keys(tax.categories).map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">Subcategory
          <select aria-label="Catalogue subcategory" className={selectClass} value={filters.subcategory}
            disabled={!filters.category} onChange={(e) => patch({ subcategory: e.target.value })}>
            <option value="">{filters.category ? 'All subcategories' : 'Choose a category first'}</option>
            {(tax.categories[filters.category] || []).map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">Occasion
          <select aria-label="Catalogue occasion" className={selectClass} value={filters.occasion}
            onChange={(e) => patch({ occasion: e.target.value })}>
            <option value="">All occasions</option>
            {tax.occasions.map((o) => <option key={o}>{o}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">Attribute
          <select aria-label="Catalogue attribute" className={selectClass} value={attribute} onChange={(e) => onAttributeChange(e.target.value)}>
            <option value="">Choose an attribute</option>
            {Object.entries(tax.attributes).map(([key, spec]) => <option key={key} value={key}>{spec.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-medium text-gray-500">Value
          <select aria-label="Catalogue attribute value" className={selectClass} value={filters.attributes[attribute] || ''}
            disabled={!attribute} onChange={(e) => setValue(e.target.value)}>
            <option value="">{attribute ? 'All values' : 'Choose an attribute first'}</option>
            {(tax.attributes[attribute]?.values || []).map((v) => <option key={v}>{v}</option>)}
          </select>
        </label>
      </div>
      {active && (
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(filters.attributes).map(([key, value]) => (
            <button key={key} onClick={() => { const attributes = { ...filters.attributes }; delete attributes[key]; patch({ attributes }); }}
              aria-label={`Remove ${tax.attributes[key].label}: ${value}`}
              className={`inline-flex items-center gap-1.5 rounded-md bg-sky-50 px-2 py-1 text-[12px] text-sky-800 ${focusRing}`}>
              {tax.attributes[key].label}: {value}<X size={12} aria-hidden />
            </button>
          ))}
          <button className={`text-[12px] text-gray-500 underline hover:text-gray-800 ${focusRing}`}
            onClick={() => { onChange(EMPTY_PRODUCT_FILTERS); onAttributeChange(''); }}>Clear filters</button>
          {Object.keys(filters.attributes).length > 0 && <span className="text-[11.5px] text-gray-400">Choose another attribute to combine filters. Products must match every filter.</span>}
        </div>
      )}
    </div>
  );
}
